import React, { createElement } from 'react';
import { XIcon, PackageIcon, DollarSignIcon, DownloadIcon } from 'lucide-react';
interface ExcessInventoryItem {
  id: string;
  reference: string;
  design: string;
  currentStock: number;
  unitCost: number;
  totalCost: number;
  priority: 'critical' | 'high' | 'medium';
}
interface ExcessInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const mockExcessItems: ExcessInventoryItem[] = [{
  id: '1',
  reference: 'PVM-HP-STD-001',
  design: 'High Profile',
  currentStock: 35,
  unitCost: 12500,
  totalCost: 437500,
  priority: 'high'
}, {
  id: '2',
  reference: 'PVM-6-5/8-GALV-002',
  design: '6" 5/8 GALV',
  currentStock: 130,
  unitCost: 15000,
  totalCost: 1950000,
  priority: 'critical'
}, {
  id: '3',
  reference: 'PVM-8-5/8-POLY-003',
  design: '8" 5/8 POLY',
  currentStock: 38,
  unitCost: 18000,
  totalCost: 684000,
  priority: 'high'
}, {
  id: '4',
  reference: 'LIN-HP-004',
  design: 'Linear High Profile',
  currentStock: 23,
  unitCost: 22000,
  totalCost: 506000,
  priority: 'medium'
}, {
  id: '5',
  reference: 'PVM-5-POLY-005',
  design: '5" POLY',
  currentStock: 42,
  unitCost: 11000,
  totalCost: 462000,
  priority: 'medium'
}];
export function ExcessInventoryModal({
  isOpen,
  onClose
}: ExcessInventoryModalProps) {
  if (!isOpen) return null;
  const handleDownloadExcel = () => {
    const headers = ['Reference', 'Design', 'Excess Stock', 'Unit Cost', 'Total Cost', 'Priority'];
    const rows = mockExcessItems.map(item => [item.reference, item.design, item.currentStock.toString(), `$${item.unitCost.toLocaleString()}`, `$${item.totalCost.toLocaleString()}`, item.priority.toUpperCase()]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `excess-inventory-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const totalExcessUnits = mockExcessItems.reduce((sum, item) => sum + item.currentStock, 0);
  const totalStagnantMoney = mockExcessItems.reduce((sum, item) => sum + item.totalCost, 0);
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-131">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-132">
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 flex items-center justify-between" data-id="element-133">
          <div className="flex items-center gap-4" data-id="element-134">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center" data-id="element-135">
              <PackageIcon className="w-6 h-6" data-id="element-136" />
            </div>
            <div data-id="element-137">
              <h2 className="text-2xl font-bold" data-id="element-138">Excess Inventory</h2>
              <p className="text-purple-100 text-sm mt-1" data-id="element-139">
                {mockExcessItems.length} references not in stock designs
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors" data-id="element-140">
            <XIcon className="w-6 h-6" data-id="element-141" />
          </button>
        </div>
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50" data-id="element-142">
          <div className="flex items-center justify-between" data-id="element-143">
            <div className="flex items-center gap-6" data-id="element-144">
              <div className="text-center" data-id="element-145">
                <p className="text-sm text-gray-600 mb-1" data-id="element-146">Total Excess Units</p>
                <p className="text-3xl font-bold text-purple-600" data-id="element-147">
                  {totalExcessUnits}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-148">machines</p>
              </div>
              <div className="w-px h-16 bg-gray-300" data-id="element-149"></div>
              <div className="text-center" data-id="element-150">
                <p className="text-sm text-gray-600 mb-1" data-id="element-151">Stagnant Capital</p>
                <p className="text-3xl font-bold text-red-600" data-id="element-152">
                  ${(totalStagnantMoney / 1000000).toFixed(2)}M
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-153">
                  ${totalStagnantMoney.toLocaleString()} total
                </p>
              </div>
              <div className="w-px h-16 bg-gray-300" data-id="element-154"></div>
              <div className="text-center" data-id="element-155">
                <p className="text-sm text-gray-600 mb-1" data-id="element-156">Critical Items</p>
                <p className="text-3xl font-bold text-gray-900" data-id="element-157">
                  {mockExcessItems.filter(i => i.priority === 'critical').length}
                </p>
              </div>
            </div>
            <button onClick={handleDownloadExcel} className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-158">
              <DownloadIcon className="w-4 h-4" data-id="element-159" />
              Download Excel
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6" data-id="element-160">
          <div className="space-y-3" data-id="element-161">
            {mockExcessItems.map(item => <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-all" data-id="element-162">
                <div className="flex items-start justify-between mb-4" data-id="element-163">
                  <div className="flex items-start gap-4 flex-1" data-id="element-164">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/30" data-id="element-165">
                      <PackageIcon className="w-6 h-6 text-white" data-id="element-166" />
                    </div>
                    <div className="flex-1" data-id="element-167">
                      <div className="flex items-center gap-3 mb-2" data-id="element-168">
                        <h3 className="text-lg font-bold text-gray-900" data-id="element-169">
                          {item.reference}
                        </h3>
                        <span className={`px-2.5 py-1 rounded text-xs font-semibold border ${getPriorityColor(item.priority)}`} data-id="element-170">
                          {item.priority.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-171">
                        {item.design}
                      </p>
                      <div className="grid grid-cols-3 gap-4" data-id="element-172">
                        <div data-id="element-173">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-174">
                            Excess Stock
                          </p>
                          <p className="text-lg font-bold text-purple-600" data-id="element-175">
                            {item.currentStock} units
                          </p>
                        </div>
                        <div data-id="element-176">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-177">
                            Unit Cost
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-178">
                            ${(item.unitCost / 1000).toFixed(0)}K
                          </p>
                        </div>
                        <div data-id="element-179">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-180">
                            Total Stagnant
                          </p>
                          <p className="text-lg font-bold text-red-600" data-id="element-181">
                            ${item.totalCost.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-3 mb-3 border border-red-200" data-id="element-182">
                  <div className="flex items-center gap-2" data-id="element-183">
                    <DollarSignIcon className="w-4 h-4 text-red-600" data-id="element-184" />
                    <p className="text-sm text-red-700 font-medium" data-id="element-185">
                      ${item.totalCost.toLocaleString()} in stagnant capital -
                      Not in stock designs
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100" data-id="element-186">
                  <button className="flex-1 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded transition-colors border border-blue-200 text-sm font-medium" data-id="element-187">
                    View Details
                  </button>
                  <button className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded hover:from-purple-600 hover:to-purple-700 transition-all text-sm font-medium shadow-sm" data-id="element-188">
                    Manage Excess
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}